import { faMoneyBill, faPerson, faRight } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { AbstractStep } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import { Button, FormCurrency, FormDecidable, FormNumber } from "bm-ui-kit";
import React from "react";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import {
	MAX_AGE,
	MAX_INCOME,
	MAX_LOAN_AMOUNT,
	MAX_MATURITY_MONTHS,
	MIN_AGE,
	MIN_INCOME,
	MIN_LOAN_AMOUNT,
	MIN_MATURITY_MONTHS,
} from "../data";

export interface StepFields {
	loanAmount: number;
	maturityMonths: number;
	monthlyIncome: number;
	hasHungarianEmploymentOrEntrepreneurRelationship: boolean;
	age: number;
}

export class Step1 extends AbstractStep<StepFields> {
	protected initValues(): StepFields {
		return {
			loanAmount: 4_000_000,
			maturityMonths: 120,
			monthlyIncome: undefined,
			hasHungarianEmploymentOrEntrepreneurRelationship: undefined,
			age: undefined,
		};
	}

	protected renderContent(): ReactNode {
		const disabled = this.isDisabled();

		return (
			<>
				<Card.Header className="text-center">
					<h4>Munkáshitel 2025</h4>
					<h5 className="mb-0">Jogosult vagy a 4 millió Ft kamatmentes kölcsönre?</h5>
				</Card.Header>
				<Card.Body>
					<FormCurrency
						label="Mennyi munkáshitelt szeretnél igényelni?"
						name="loanAmount"
						className="mt-3"
						validate={this.validLoanAmount}
						prepend={<FontAwesomeIcon icon={faMoneyBill} />}
						disabled={disabled}
						required
					/>

					<FormNumber
						label="Milyen futamidővel vennéd fel a kölcsönt?"
						name="maturityMonths"
						validate={this.validMaturityMonths}
						className="my-3"
						disabled={disabled}
						append="hónap"
						required
					/>

					<FormCurrency
						label="Mennyi a havi jövedelmed?"
						name="monthlyIncome"
						validate={this.validMonthlyIncome}
						prepend={<FontAwesomeIcon icon={faMoneyBill} />}
						disabled={disabled}
						required
					/>

					<FormDecidable
						label="Van magyar alkalmazotti, vállalkozói jogviszonyod?"
						name="hasHungarianEmploymentOrEntrepreneurRelationship"
						className="my-2"
						disabled={disabled}
						infoTooltip="Részmunkaidő is elfogadható, amennyiben legalább heti 20 órában foglalkoztatnak."
						required
					/>
					<FormNumber
						label="Hány éves vagy?"
						name="age"
						prepend={<FontAwesomeIcon icon={faPerson} />}
						disabled={disabled}
						validate={this.validAge}
						required
					/>
				</Card.Body>
				<Card.Footer className="text-center">
					<Button
						type="submit"
						icon={<FontAwesomeIcon icon={faRight} />}
						className="text-uppercase text-white"
						variant="primary"
					>
						Tovább
					</Button>
				</Card.Footer>
			</>
		);
	}

	private validMonthlyIncome(monthlyIncome: number): string {
		if (isNaN(monthlyIncome)) {
			return `Kérjük add meg a havi jövedelmed!`;
		}

		if (monthlyIncome < MIN_INCOME) {
			return `A havi jövedelem nem lehet kevesebb mint ${formatText(MIN_INCOME, "ft")}!`;
		}

		if (monthlyIncome > MAX_INCOME) {
			return `A havi jövedelem nem lehet több mint ${formatText(MAX_INCOME, "ft")}!`;
		}
	}

	private validMaturityMonths(maturityMonths: number): string {
		if (isNaN(maturityMonths)) {
			return `Kérjük add meg a futamidőt (hónapokban)!`;
		}

		if (maturityMonths < MIN_MATURITY_MONTHS) {
			return `A futamidő nem lehet kevesebb mint ${formatText(MIN_MATURITY_MONTHS, "hónap")}!`;
		}

		if (maturityMonths > MAX_MATURITY_MONTHS) {
			return `A futamidő nem lehet több mint ${formatText(MAX_MATURITY_MONTHS, "hónap")}!`;
		}
	}

	private validLoanAmount(loanAMount: number): string {
		if (isNaN(loanAMount)) {
			return `Kérjük add meg a hitelösszeget!`;
		}

		if (loanAMount < MIN_LOAN_AMOUNT) {
			return `A hitelösszeg nem lehet kevesebb mint ${formatText(MIN_LOAN_AMOUNT, "ft")}!`;
		}

		if (loanAMount > MAX_LOAN_AMOUNT) {
			return `A hitelösszeg nem lehet több mint ${formatText(MAX_LOAN_AMOUNT, "ft")}!`;
		}
	}

	private validAge(age: number): string {
		if (isNaN(age)) {
			return `Kérjük add meg az életkorod!`;
		}

		if (age < MIN_AGE) {
			return `Az életkorod nem lehet kevesebb mint ${formatText(MIN_AGE, "év")}!`;
		}

		if (age > MAX_AGE) {
			return `Az életkorod nem lehet több mint ${formatText(MAX_AGE, "év")}!`;
		}
	}

	protected getComponentName(): string {
		return "Step1";
	}
}
